import {
    InterestTypes,
    IProfileOptionsResponse,
    Persona,
} from '@kcftech/prometheustypescriptapiclient';
import {
    DropdownCustomInputInlineEditor,
    OptionChild,
} from 'components/inline_edit/DropdownCustomInputInlineEditor';
import { MultiSelectInlineEditor } from 'components/inline_edit/MultiSelectInlineEditor';
import { personaIsOther } from 'components/modal/persona/PersonaModal';
import { maxLength } from 'components/modal/persona/PersonaModalPage1';
import { usePermissions } from 'contexts/PermissionsContext';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';

import { ProfileItem, ProfileSection } from '../components';
import { UserSettingsFormData } from './UserProfileModal';

export type PersonaOptionsPaneProps = Readonly<{
    options: IProfileOptionsResponse;
    className?: string;
    control: Control<UserSettingsFormData>;
    setValue: UseFormSetValue<UserSettingsFormData>;
}>;

export function PersonaOptionsPane({
    options: { Personas, PersonaInterests },
    className,
    control,
    setValue,
}: PersonaOptionsPaneProps) {
    const { canEditUserPersonas } = usePermissions();

    const persona = useWatch({ control, name: 'profile.Persona' });
    const customPersona = useWatch({ control, name: 'profile.CustomPersona' });
    const personaInterests = useWatch({ control, name: 'profile.PersonaInterests' });

    const dataOptions: OptionChild<Persona>[] = Personas.map((p) => ({
        Value: p.Value,
        Text: p.Text,
    }));

    if (!canEditUserPersonas) {
        return null;
    }

    return (
        <ProfileSection className={className} title="Role/Interest">
            <ProfileItem label="Your Primary Role">
                <DropdownCustomInputInlineEditor<Persona>
                    customOption={Persona.Other}
                    values={{
                        dropdownValue: persona,
                        custom: customPersona,
                    }}
                    onConfirm={(value) => {
                        setValue('profile.Persona', value?.dropdownValue);
                        setValue(
                            'profile.CustomPersona',
                            personaIsOther(value?.dropdownValue)
                                ? value.custom
                                : undefined
                        );
                    }}
                    dropdownConfig={{
                        data: dataOptions,
                        renderLabel: ({ value }) => value?.Text,
                        renderItem: ({ item }) => item.Text,
                        dataKey: (v) => v.Text,
                    }}
                    textInputConfig={{
                        customLabel: 'Specify Your Primary Role',
                        maxLength,
                    }}
                    validate={(value) => {
                        if (personaIsOther(value?.dropdownValue)) {
                            if (!value.custom) {
                                return 'Role is required';
                            }
                            if (value.custom.length > maxLength) {
                                return `Role cannot exceed ${maxLength} characters`;
                            }
                        }
                        return undefined;
                    }}
                />
            </ProfileItem>
            <ProfileItem label="Areas of Interest">
                <MultiSelectInlineEditor
                    multiSelectConfig={{
                        options: PersonaInterests.map((p) => ({
                            value: InterestTypes[p.Value as keyof typeof InterestTypes],
                            title: p.Text,
                        })),
                        labelPrefersMinWidth: true,
                    }}
                    value={personaInterests?.map((p) => ({
                        value: p,
                        title:
                            PersonaInterests.find(
                                (i) => i.Value.toString() === p.toString()
                            )?.Text ?? p.toString(),
                    }))}
                    onConfirm={(value) =>
                        setValue(
                            'profile.PersonaInterests',
                            value?.map(
                                (v) =>
                                    InterestTypes[v.value as keyof typeof InterestTypes]
                            )
                        )
                    }
                />
            </ProfileItem>
        </ProfileSection>
    );
}
